<script lang="ts" setup>
  import {
    Combobox,
    type ComboboxInputValueChangeDetails,
    type ComboboxInteractOutsideEvent,
    type ComboboxOpenChangeDetails,
    type ComboboxValueChangeDetails,
    createListCollection,
  } from '@ark-ui/vue';
  import { useLocale } from '@tundr/i18n';
  import { computed, ref, useId, watch } from 'vue';
  import { visuallyHiddenClass } from '../../_shared/styles/visually-hidden.css';
  import { GLOBAL_OVERLAYS_TARGET } from '../../config/global-overlays-target';
  import {
    dropdownContentClass,
    dropdownContentSameWidthClass,
    dropdownContentSelfScrollClass,
    dropdownPositionerClass,
  } from '../../Dropdown/Dropdown.css';
  import { Loader, LoaderSize } from '../../Loader';
  import { FieldSize, FormField } from '../FormField';
  import {
    fieldClass,
    fieldSmallClass,
    iconClass,
    inputClass,
    inputSmallClass,
  } from '../TextInput/TextInput.css';
  import {
    autocompleteFooterClass,
    autocompleteListClass,
    autocompleteMessageClass,
    autocompleteOptionClass,
    autocompleteOptionDescriptionClass,
    autocompleteOptionLabelClass,
    autocompleteOptionTextClass,
    autocompletePanelClass,
  } from './Autocomplete.css';
  import {
    AUTOCOMPLETE_DEFAULT_MIN_CHARS,
    type AutocompleteEmits,
    type AutocompleteOption,
    type AutocompleteProps,
    type AutocompleteSlots,
  } from './Autocomplete.types';

  const props = withDefaults(defineProps<AutocompleteProps>(), {
    modelValue: null,
    options: () => [],
    loading: false,
    minChars: AUTOCOMPLETE_DEFAULT_MIN_CHARS,
    disabled: false,
    required: false,
    optionalLabel: false,
    meta: undefined,
    size: FieldSize.MD,
    open: undefined,
    disableTeleport: false,
  });

  const emit = defineEmits<AutocompleteEmits>();

  const slots = defineSlots<AutocompleteSlots>();

  const { t } = useLocale('ds-components');

  const uid = useId();
  const fieldId = computed(() => props.id ?? `ds-autocomplete-${uid}`);

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const inputText = ref(props.modelValue ?? '');
  const internalOpen = ref(false);
  const isOpen = computed(() => props.open ?? internalOpen.value);

  const setOpen = (open: boolean) => {
    if (open === isOpen.value) return;
    internalOpen.value = open;
    emit('update:open', open);
  };

  watch(
    () => props.modelValue,
    (value) => {
      inputText.value = value ?? '';
    },
  );

  watch(
    () => props.open,
    (open) => {
      if (open !== undefined) internalOpen.value = open;
    },
  );

  const trimmedQuery = computed(() => inputText.value.trim());

  const hasMinChars = computed(
    () => trimmedQuery.value.length >= props.minChars,
  );

  const visibleOptions = computed<AutocompleteOption[]>(() =>
    hasMinChars.value ? props.options : [],
  );

  const collection = computed(() =>
    createListCollection<AutocompleteOption>({
      items: visibleOptions.value,
      itemToValue: (option) => option.value,
      itemToString: (option) => option.label,
    }),
  );

  const onInputValueChange = ({
    inputValue,
    reason,
  }: ComboboxInputValueChangeDetails) => {
    if (inputValue === inputText.value) return;
    inputText.value = inputValue;
    if (!trimmedQuery.value) setOpen(false);
    emit('update:modelValue', inputValue);
    if (reason === 'input-change') emit('search', inputValue);
  };

  const onValueChange = ({ value }: ComboboxValueChangeDetails) => {
    const option = props.options.find((item) => item.value === value[0]);
    if (!option) return;
    inputText.value = option.label;
    emit('update:modelValue', option.label);
    emit('select', option);
  };

  const onOpenChange = ({ open }: ComboboxOpenChangeDetails) => {
    setOpen(open && trimmedQuery.value.length > 0);
  };

  const positioning = { placement: 'bottom-start', sameWidth: true } as const;

  const isEmpty = computed(
    () => hasMinChars.value && !props.loading && !visibleOptions.value.length,
  );

  const showLoadingMessage = computed(
    () => hasMinChars.value && props.loading && !visibleOptions.value.length,
  );

  const panelMessage = computed(() => {
    if (!trimmedQuery.value) return '';
    if (!hasMinChars.value) return t('autocomplete.min_chars');
    if (showLoadingMessage.value) return t('autocomplete.loading');
    if (isEmpty.value) {
      return props.noResultsMessage ?? t('autocomplete.no_results');
    }
    return '';
  });

  const hasPanelContent = computed(
    () => visibleOptions.value.length > 0 || Boolean(panelMessage.value),
  );

  const messagesAllowed = computed(() =>
    props.meta ? !props.meta.valid && props.meta.touched : true,
  );

  const visibleWarning = computed(() => {
    if (props.error && messagesAllowed.value) return '';
    return props.warning && messagesAllowed.value ? props.warning : '';
  });

  const liveMessage = computed(() => {
    if (!isOpen.value) return visibleWarning.value;
    if (props.loading || !trimmedQuery.value) return '';
    if (panelMessage.value) return panelMessage.value;
    return t('autocomplete.results_count', {
      count: visibleOptions.value.length,
    });
  });

  const accessibleName = computed(() => props.label ?? props.ariaLabel);

  const optionAccessibleName = (option: AutocompleteOption) =>
    !slots.option && option.description
      ? `${option.label}, ${option.description}`
      : undefined;

  const panelRef = ref<HTMLElement | null>(null);

  const onInteractOutside = (event: ComboboxInteractOutsideEvent) => {
    const target = event.detail.target as Node | null;
    if (target && panelRef.value?.contains(target)) event.preventDefault();
  };

  const testIdPrefix = computed(() => props.testId || props.name);
</script>

<template>
  <FormField
    :id="fieldId"
    :label="label"
    :optional-label="optionalLabel"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :meta="meta"
    :size="size"
    :disabled-reason="disabled ? disabledReason : undefined"
    :announce-warning="false"
  >
    <template v-if="slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>

    <template #default="{ describedBy, hasError, labelId }">
      <Combobox.Root
        :ids="{ input: fieldId }"
        :name="name"
        :collection="collection"
        :input-value="inputText"
        :model-value="[]"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder"
        :open="isOpen && hasPanelContent"
        :positioning="positioning"
        input-behavior="autohighlight"
        selection-behavior="preserve"
        allow-custom-value
        @input-value-change="onInputValueChange"
        @value-change="onValueChange"
        @open-change="onOpenChange"
        @interact-outside="onInteractOutside"
      >
        <Combobox.Control
          :class="[fieldClass, isSmall && fieldSmallClass]"
          :data-invalid="hasError ? '' : undefined"
          :data-disabled="disabled ? '' : undefined"
        >
          <span v-if="slots.leadingIcon" :class="iconClass">
            <slot name="leadingIcon" />
          </span>
          <Combobox.Input
            :class="[inputClass, isSmall && inputSmallClass]"
            :data-test-id="testId"
            :aria-label="label ? undefined : ariaLabel"
            :aria-invalid="hasError ? 'true' : undefined"
            :aria-describedby="describedBy"
            @focus="(event: FocusEvent) => emit('focus', event)"
            @blur="(event: FocusEvent) => emit('blur', event)"
          />
          <span v-if="slots.icon" :class="iconClass">
            <slot name="icon" />
          </span>
        </Combobox.Control>

        <div :class="visuallyHiddenClass" role="status" aria-live="polite">
          {{ liveMessage }}
        </div>

        <Teleport
          defer
          :disabled="disableTeleport"
          :to="`.${GLOBAL_OVERLAYS_TARGET}`"
        >
          <Combobox.Positioner :class="dropdownPositionerClass">
            <div
              ref="panelRef"
              :class="[
                dropdownContentClass,
                dropdownContentSameWidthClass,
                dropdownContentSelfScrollClass,
                autocompletePanelClass,
              ]"
              :hidden="!isOpen || !hasPanelContent || undefined"
              :data-test-id="`${testIdPrefix}-autocomplete-panel`"
              @pointerdown.prevent
            >
              <Combobox.Content
                :class="autocompleteListClass"
                :aria-labelledby="labelId ?? undefined"
                :aria-label="labelId ? undefined : accessibleName"
                :aria-busy="loading || undefined"
              >
                <Combobox.Item
                  v-for="option in visibleOptions"
                  :key="option.value"
                  :item="option"
                  :class="autocompleteOptionClass"
                  :aria-label="optionAccessibleName(option)"
                  :data-test-id="`${testIdPrefix}-autocomplete-option-${option.value}`"
                >
                  <Combobox.ItemContext v-slot="item">
                    <slot
                      name="option"
                      :option="option"
                      :query="inputText"
                      :highlighted="item.highlighted"
                    >
                      <span :class="autocompleteOptionTextClass">
                        <span
                          :class="autocompleteOptionLabelClass"
                          :title="option.label"
                          >{{
                            option.label
                          }}</span
                        >
                        <span
                          v-if="option.description"
                          :class="autocompleteOptionDescriptionClass"
                          :title="option.description"
                          >{{
                            option.description
                          }}</span
                        >
                      </span>
                    </slot>
                  </Combobox.ItemContext>
                </Combobox.Item>
              </Combobox.Content>

              <div
                v-if="panelMessage"
                :class="autocompleteMessageClass"
                :data-test-id="`${testIdPrefix}-autocomplete-message`"
              >
                <slot v-if="isEmpty && slots.empty" name="empty" />
                <template v-else>
                  <Loader
                    v-if="showLoadingMessage"
                    :size="LoaderSize.SM"
                    :announced="false"
                    aria-hidden="true"
                  />
                  <span aria-hidden="true">{{ panelMessage }}</span>
                </template>
              </div>

              <div v-if="slots.footer" :class="autocompleteFooterClass">
                <slot name="footer" />
              </div>
            </div>
          </Combobox.Positioner>
        </Teleport>
      </Combobox.Root>
    </template>
  </FormField>
</template>
